import h from 'hyperscript';
import { unByKey as unByKeyOl } from 'ol/Observable';
import stopwatchIcon from '@disy/cadenza-icons/stopwatch.svg';
import filterSmallIcon from '@disy/cadenza-icons/filter-small.svg';
import zoomToScale from '@disy/cadenza-icons/zoom-to-scale.svg';

import 'ui/alert/alert';
import 'ui/switch/switch';

import { context } from 'cadenza/map/context/context';
import { TimelineVisualization2dAction } from 'cadenza/map/actions/timeline-visualization-2d-action';
import { getActionContext } from 'cadenza/action-context';
import { LayerActionToolbar } from 'cadenza/map/layer-action-toolbar/layer-action-toolbar';
import { Filter } from 'cadenza/map/layer-actions/filter';
import { RemoveLayer } from 'cadenza/map/layer-actions/remove-layer';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { ClassicSketchLayer } from 'cadenza/map/classic-sketch-layer/classic-sketch-layer';
import { icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';
import { LegendLayerTasks } from 'cadenza/map/legend/layer/legend-layer-tasks';
import {
  getLayerTasks,
  LAYER_EVENT_ALL_TASKS_COMPLETED,
  LAYER_EVENT_TASK_COMPLETED,
  LAYER_EVENT_TASKS_ONGOING
} from 'cadenza/map/layer-tasks';
import { refreshLayer, zoomToVisible } from 'cadenza/map/layer-utils';
import { LegendImage } from 'cadenza/map/legend/legend-image';
import { LegendLayerAlerts } from 'cadenza/map/legend/layer/legend-layer-alerts';
import { createExtendedInfoElement } from 'cadenza/map/legend/layer/extended-info/legend-layer-extended-info';
import { px } from 'cadenza/utils/px';
import { SketchControls } from 'cadenza/map/legend/layer/sketch-controls/sketch-controls';
import { unByKey } from 'cadenza/utils/event-util';

import i18n from '../messages.properties';

import './legend-layer.css';

const COMPONENT_NAME = 'd-map-layer';

export class LegendLayer extends HTMLElement {

  #layerTasks;
  #layerActions;
  #layerRemoval;
  #timelineVisualization;
  #graphicalLegendPart;
  #extendedInfoElement;
  #map;
  #layerActionToolbar;
  #visibleSwitch;
  #name;
  #timelineButtonSpan;
  #filterButtonSpan;
  #openLayersEventKeys;
  #eventKeys;
  #canceledWhenMadeEffectivelyInvisible;

  constructor (layer, map, { layerActions = true, layerRemoval = true, timelineVisualization = false, inCustomAppContext = false } = {}) {
    super();
    this.layer = layer;
    this.#map = map;
    this.#layerActions = layerActions;
    this.#layerRemoval = layerRemoval;
    this.#timelineVisualization = timelineVisualization;
    this.#layerTasks = getLayerTasks(layer);
    const layerID = layer.get(LayerProperty.ID);
    const id = `${layerID}-name`;

    this.setAttribute('role', 'listitem');
    this.setAttribute('aria-labelledby', id);
    this.classList.add(COMPONENT_NAME);
    this.dataset.testid = 'map-legend-layer';
    const titleRow = this.#createTitleRow(id);

    const emptyRequiredConditionsWarning = this.#createEmptyRequiredConditionsWarning(layer);
    this.#extendedInfoElement = createExtendedInfoElement(layer.get(LayerProperty.EXTENDED_INFO));
    this.#graphicalLegendPart = new LegendImage(this.layer, this.#map);
    this.alerts = new LegendLayerAlerts(layer, map);

    this.tasks = new LegendLayerTasks(layer);
    this.tasks.hidden = true;

    this.append(
      titleRow,
      this.alerts,
      this.tasks,
      emptyRequiredConditionsWarning,
      this.#extendedInfoElement,
      this.#graphicalLegendPart);

    this.#updateTimelinePlayerButton();

    if (layer.get(LayerProperty.IS_FILTERABLE)) {
      this.#configureLayerFilter(map, layer, layerActions, emptyRequiredConditionsWarning);
    }

    if (layerRemoval) {
      const removeLayerAction = new RemoveLayer(layer, { map });
      titleRow.append(this.#createLayerRemovalButton(layerID, removeLayerAction));
    }

    if (layerActions) {
      this.#layerActionToolbar = new LayerActionToolbar(layer, map, { inCustomAppContext });
      this.insertBefore(this.#layerActionToolbar, this.tasks);
    }

    const toggleChoices = layer.get(LayerProperty.TOGGLE_CHOICES);
    if (toggleChoices) {
      this.insertBefore(
        this.#createLayerChoices(layer, toggleChoices),
        this.#graphicalLegendPart
      );
    }

    const normalGroupChoices = layer.get(LayerProperty.NORMAL_GROUP_CHOICES);
    if (normalGroupChoices) {
      this.insertBefore(
        this.#createNormalGroupChoices(normalGroupChoices, layer),
        this.#graphicalLegendPart
      );
    }

    if (layer instanceof ClassicSketchLayer) {
      this.insertBefore(new SketchControls(this.layer, this.#map), this.tasks);
      layer.on('change:editing', this.#onChangedEditing.bind(this));
      this.#onChangedEditing();
    }

    // if there are selectable group layers the normal legend image is not shown
    if (!normalGroupChoices) {
      this.#setUpImageLoadTaskHandling();
    }
  }

  #createTitleRow (id) {
    this.#visibleSwitch = h(`d-switch.${COMPONENT_NAME}-visible.qa-layer-visible`, {
      onclick: (event) => event.stopPropagation(),
      onchange: () => this.layer.setVisible(this.#visibleSwitch.checked)
    });
    this.#name = h(`span.${COMPONENT_NAME}--name-inner.qa-layer-name`, { id });
    this.#timelineButtonSpan = h('span');
    this.#filterButtonSpan = h('span');
    return h('.d-map-layer--title-row.d-stack-h.space-1', [
      this.#visibleSwitch,
      h(`button.button.button-s.button-icon.button-borderless.${COMPONENT_NAME}-zoom-to-scale`, {
        title: i18n('layer.zoomToVisibleBtn'),
        onclick: event => {
          event.stopPropagation();
          zoomToVisible(this.#map, this.layer);
        }
      }, icon(zoomToScale)),
      h(`span.${COMPONENT_NAME}--name-outer`,
        this.#name,
        this.#timelineButtonSpan,
        this.#filterButtonSpan)
    ]);
  }

  #configureLayerFilter (map, layer, layerActions, emptyRequiredConditionsWarning) {
    const onPropertyChange = (event) => {
      if (event.key === LayerProperty.IS_FILTERED) {
        toggleFilterButtonOrHint(map, layer, layerActions, this.#filterButtonSpan, this);
      }
      emptyRequiredConditionsWarning.hidden =
          !event.target.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS);
    };
    layer.on('propertychange', onPropertyChange);
    toggleFilterButtonOrHint(map, layer, layerActions, this.#filterButtonSpan, this);
  }

  #createLayerRemovalButton (layerID, removeLayerAction) {
    return h('button.button.button-s.button-icon.button-borderless.d-map-layer--remove-button', {
      type: 'button',
      id: `legend-layer-action-${layerID}-removeLayer`,
      title: removeLayerAction.label,
      onclick: (event) => {
        event.stopPropagation();
        removeLayerAction.execute().catch(error => logger.error(`Did not remove layer due to: ${error ? error.message : ''}`));
      }
    }, removeLayerAction.icon);
  }

  /**
   * @param layer
   * @param toggleChoices
   * @return {Node}
   */
  #createLayerChoices (layer, toggleChoices) {
    return h('fieldset.d-layer--choices',
      {
        onclick: event => event.stopPropagation(),
        onchange: event => {
          this.layer.set(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX, event.target.value);
          context(this.#map).push().then(() => {
            context(this.#map).pull();
          }).catch(error => logger.error('Failed to update layer change', error));
        }
      },
      createToggleLayerChoices(
        layer.get(LayerProperty.ID),
        layer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX),
        toggleChoices
      )
    );
  }

  /**
   * @param normalGroupChoices
   * @param layer
   * @return {Node}
   */
  #createNormalGroupChoices (normalGroupChoices, layer) {
    return h('fieldset.d-layer--choices',
      {
        onclick: event => event.stopPropagation(),
        onchange: event => {
          const checked = event.target.checked;
          const uid = event.target.uid;
          normalGroupChoices.forEach(groupChoice => {
            if (groupChoice.uid === uid) {
              groupChoice.visible = checked;
            }
          });
          context(this.#map).push().then(() => {
            context(this.#map).pull();
          }).catch(error => logger.error('Failed to update layer change', error));
        }
      },
      createGroupLayerChoices(
        layer.get(LayerProperty.ID),
        normalGroupChoices
      )
    );
  }

  #createEmptyRequiredConditionsWarning (layer) {
    return h(
      'd-alert.d-alert-small.d-alert-warning',
      { hidden: !layer.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS) },
      i18n('layer.warnings.hasEmptyRequiredConditions'));
  }

  // SortableJS clones the node, which is dragged.
  // This doesn't work properly with this complex custom element, hence the override.
  cloneNode () {
    const options = {
      layerActions: this.#layerActions,
      layerRemoval: this.#layerRemoval,
      timelineVisualization: this.#timelineVisualization
    };
    const legendLayer = new LegendLayer(this.layer, this.#map, options);
    legendLayer.connectedCallback();

    if (this.#layerActionToolbar) {
      const destActionToolbar = legendLayer.querySelector('d-layer-action-toolbar');
      const srcActionToolbar = this.querySelector('d-layer-action-toolbar');
      const clonedActionToolbar = srcActionToolbar.cloneNode(true);
      legendLayer.replaceChild(clonedActionToolbar, destActionToolbar);
    }

    if (this.tasks) {
      const tasks = legendLayer.querySelector('d-layer-tasks');
      const clonedTasks = tasks.cloneNode(true);
      legendLayer.replaceChild(clonedTasks, tasks);
    }

    if (this.#extendedInfoElement && this.#extendedInfoElement.childNodes?.length > 0) {
      const clonedExtendedInfos = [];
      for (const extendedInfo of this.#extendedInfoElement.childNodes) {
        clonedExtendedInfos.push(extendedInfo.cloneNode());
      }
      legendLayer.#extendedInfoElement.innerHTML = '';
      legendLayer.#extendedInfoElement.append(...clonedExtendedInfos);
    }

    return legendLayer;
  }

  #setUpImageLoadTaskHandling () {
    context(this.layer).registerImageLoadTaskHandling();
    if (this.#layerTasks.hasOngoingTasks()) {
      this.tasks.hidden = false;
      this.#graphicalLegendPart.hidden = true;
    }
    this.layer.on(LAYER_EVENT_TASKS_ONGOING, () => {
      this.tasks.hidden = false;
      this.#graphicalLegendPart.hidden = true;
    });
    this.layer.on(LAYER_EVENT_ALL_TASKS_COMPLETED, () => {
      this.tasks.hidden = true;
      this.#graphicalLegendPart.hidden = false;
      // prevent size change when the loading spinner replaces the image the next time
      this.tasks.style.minHeight = px(this.#graphicalLegendPart.offsetHeight);
    });
    this.layer.on(LAYER_EVENT_TASK_COMPLETED, () => {
      this.#updateTimelinePlayerButton();
    });
  }

  #updateTimelinePlayerButton () {
    const showTimelinePlayerButtonId = `legend-layer-action-${this.layer.get(LayerProperty.ID)}-showTimelinePlayer`;
    if (this.#timelineVisualization
      && this.layer.get(LayerProperty.HAS_TIMELINES)
      && !this.#timelineButtonSpan.querySelector(`#${showTimelinePlayerButtonId}`.replace('~', '\\~'))) {
      const showTimelinePlayerAction = new TimelineVisualization2dAction(this.#map);

      const timelineButton = h('button.button.button-icon.button-borderless.d-map-layer--tiny-layer-action-button', {
        type: 'button',
        id: showTimelinePlayerButtonId,
        title: showTimelinePlayerAction.label,
        onclick: event => {
          event.stopPropagation();
          showTimelinePlayerAction.execute();
        }
      }, icon(stopwatchIcon));
      this.#timelineButtonSpan.append(timelineButton);

      const onChangeSupported = () => {
        showTimelinePlayerAction.isSupported(getActionContext(this.#map))
          .then(supported => { timelineButton.disabled = !supported; })
          .catch(() => { timelineButton.disabled = true; });
      };
      showTimelinePlayerAction.on('change:supported', onChangeSupported);
      onChangeSupported();
    }
  }

  #getImgLoadExecutionHelper () {
    return this.layer.get(LayerProperty.IMG_LOAD_EXECUTION_HELPER);
  }

  connectedCallback () {
    this.#onChange();
    this.#openLayersEventKeys = [
      this.layer.on(`change:${LayerProperty.NAME}`, () => this.#onChange()),
      this.layer.on(`change:${LayerProperty.VISIBLE}`, () => this.#onChangeVisible()),
      this.layer.getSource().on('change', () => this.#onChange()),
      this.#map.on(`change:${MapProperty.SELECTED_LAYER}`, () => this.#onChange()),
      this.#map.on('moveend', () => this.#onChange())
    ];
    // Fix only classic layer legend: visibility change event by openlayers-layer triggers simultaniously map-context#push
    // and reload of the legend image. However, the legend image endpoint now takes the layer visibility into account and it can
    // happen that the layer is not yet visible. This is a simple solution to reload the legend image after the push
    // because it is relevant only for cadenza web classic.
    this.#eventKeys = [
      context(this.#map).on('mapStatePushed', () => {
        if (this.#visibleSwitch.checked) {
          this.#graphicalLegendPart.reload();
        }
      })
    ];
  }

  #onChangeVisible () {
    if (this.layer.get(LayerProperty.IN_RANGE)) {
      this.#onChangedEffectiveVisibility(this.layer.get(LayerProperty.VISIBLE));
    }
    this.#onChange();
  }

  disconnectedCallback () {
    unByKeyOl(this.#openLayersEventKeys);
    unByKey(this.#eventKeys);
  }

  #onChange () {
    if (context(this.#map).layers.includes(this.layer)) {
      const layer = this.layer;

      this.#visibleSwitch.checked = layer.getVisible();
      this.#visibleSwitch.title = i18n(`layer.visible.${layer.getVisible()}.title`);
      this.classList.toggle('is-visible', layer.getVisible());

      const isLayerInRange = this.#isLayerInRange();
      this.classList.toggle('is-in-range', isLayerInRange);
      layer.set(LayerProperty.IN_RANGE, isLayerInRange);

      this.classList.toggle('has-empty-required-condition', this.layer.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS));

      this.#name.textContent = layer.get(LayerProperty.NAME);

      this.classList.toggle('is-selected', layer === this.#map.get(MapProperty.SELECTED_LAYER));
    }
  }

  #onChangedEffectiveVisibility (visibleAndInRange) {
    if (!visibleAndInRange) {
      const imgExecutionHelper = this.#getImgLoadExecutionHelper();
      if (imgExecutionHelper) {
        this.#canceledWhenMadeEffectivelyInvisible = imgExecutionHelper.cancelCurrent();
        /** @type LayerContext */ const layerContext = context(this.layer);
        layerContext.removeCurrentImageLoadTask();
      }
    } else if (this.#canceledWhenMadeEffectivelyInvisible) {
      // When the map image loading has been canceled when the layer was zoomed out of range or
      // switched to invisible, it has to be explicitly reloaded when it is visible again.
      // Otherwise, Open Layers reuses the blank image from the canceling when the layer is switched
      // to visible again or the zoom into range goes to the same extent as when the layer
      // was last in range.
      refreshLayer(this.layer);
    }
  }

  #onChangedEditing () {
    this.#visibleSwitch.disabled = this.layer.isEditing();
  }

  #isLayerInRange () {
    return context(this.layer).getInRange(this.#map.getView().getResolution());
  }

}

customElements.define(COMPONENT_NAME, LegendLayer);

export function getLegendLayer (layerId) {
  const legend = document.getElementById('legend');
  if (legend) {
    return legend.getLegendLayerById(layerId);
  }
}

function createToggleLayerChoices (layerId, activeLayerIndex, layerChoices) {
  return layerChoices.map(([ id, name ], layerIndex) =>
    h(
      'label.d-form-check',
      [ h(
        'input',
        {
          type: 'radio',
          name: layerId,
          value: layerIndex,
          checked: (layerIndex === activeLayerIndex)
        }
      ) ],
      name
    )
  );
}

function createGroupLayerChoices (layerId, layerChoices) {
  return layerChoices.map(({ uid, name, visible }) =>
    h('label.d-form-check', [
      h('d-switch', {
        uid,
        checked: visible
      }) ],
    name)
  );
}

function toggleFilterButtonOrHint (map, layer, layerActions, filterButtonSpan, legendLayer) {
  const layerID = layer.get(LayerProperty.ID);
  const isFiltered = layer.get(LayerProperty.IS_FILTERED);
  if (layerActions) {
    toggleFilterButton(isFiltered, layerID, filterButtonSpan, map, layer, legendLayer);
  } else {
    // CADENZA-25834 If there are no layerActions defined don't show the filter button because the filter panel won't work
    // without other changes and we currently don't have the budget to investigate and get this working correctly.
    // This for example is the case for ClientObjectSelection/CreateClientGeometry/EditClientGeometry
    toggleFilterHint(isFiltered, layerID, filterButtonSpan);
  }
}

function toggleFilterButton (isFiltered, layerID, filterButtonSpan, map, layer, legendLayer) {
  const filterButtonId = `legend-layer-action-${layerID}-showFilter`;
  if (isFiltered) {
    const filterAction = new Filter(layer, { map });
    const filterButton = h('button.button.button-icon.button-borderless.d-map-layer--tiny-layer-action-button', {
      type: 'button',
      id: filterButtonId,
      title: filterAction.label,
      onclick: event => {
        event.stopPropagation();
        if (layer !== map.get(MapProperty.SELECTED_LAYER)) {
          legendLayer.click(); // Change selected layer to current layer
        }
        setTimeout(() => filterAction.execute(), 500); // Wait a little bit to let JSF render the filter panel before start filter action
      }
    }, icon(filterSmallIcon));
    filterButtonSpan.append(filterButton);
  } else {
    removeChildren(filterButtonSpan);
  }
}

function toggleFilterHint (isFiltered, layerID, filterButtonSpan) {
  if (isFiltered) {
    const filterHintWithIcon = h('span.d-map-layer--tiny-layer-action-button.d-map-layer--filter-hint', {
      id: `legend-layer-${layerID}-filter-hint`,
      title: i18n('layer.filterHint.title')
    }, icon(filterSmallIcon));
    filterButtonSpan.append(filterHintWithIcon);
  } else {
    removeChildren(filterButtonSpan);
  }
}

function removeChildren (filterButtonSpan) {
  filterButtonSpan.innerHTML = '';
}
